import { isAfter, isBefore, isValid, parseISO } from "date-fns";
import {
  DEFAULT_FARMLAND_FILTER,
  FARMLAND_CATEGORICAL_FILTER_CONFIGS,
} from "@/features/map/constants/farmland";
import {
  type FarmlandCategoricalFilterConfig,
  type FarmlandFilterValues,
  type FarmlandTileProperties,
} from "@/features/map/types/farmland";

const AREA_UNIT_MULTIPLIER: Record<FarmlandFilterValues["areaUnit"], number> = {
  squareMeter: 1,
  are: 100,
};

const normalizeValue = (value: string | number | null | undefined): string => {
  return value === null || value === undefined ? "" : String(value).trim();
};

const parseNumber = (
  value: string | number | null | undefined
): number | null => {
  const normalizedValue = normalizeValue(value);
  if (!normalizedValue) return null;

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const parseDate = (value: string | number | null | undefined): Date | null => {
  const normalizedValue = normalizeValue(value);
  if (!normalizedValue) return null;

  const date = parseISO(normalizedValue);
  return isValid(date) ? date : null;
};

const selectedValueSet = (
  config: FarmlandCategoricalFilterConfig,
  selectedIds: string[]
): Set<string> => {
  const selectedIdSet = new Set(selectedIds);
  return new Set(
    config.options
      .filter((option) => selectedIdSet.has(option.id))
      .flatMap((option) => option.values)
      .map((value) => normalizeValue(value))
  );
};

const toSquareMeter = (
  value: number | null,
  unit: FarmlandFilterValues["areaUnit"]
): number | null => {
  return value === null ? null : value * AREA_UNIT_MULTIPLIER[unit];
};

const matchesAreaFilter = (
  properties: FarmlandTileProperties,
  areaFrom: number | null,
  areaTo: number | null
): boolean => {
  if (areaFrom === null && areaTo === null) return true;

  const area = parseNumber(properties.area_on_registry);
  if (area === null) return false;

  if (areaFrom !== null && area < areaFrom) return false;
  if (areaTo !== null && area > areaTo) return false;
  return true;
};

const matchesEndStagesDateFilter = (
  properties: FarmlandTileProperties,
  filter: FarmlandFilterValues
): boolean => {
  if (filter.endStagesDateFrom === null && filter.endStagesDateTo === null) {
    return true;
  }

  const endStagesDate = parseDate(properties.end_stages_date);
  if (endStagesDate === null) return false;

  const endStagesDateFrom = parseDate(filter.endStagesDateFrom);
  const endStagesDateTo = parseDate(filter.endStagesDateTo);

  if (
    endStagesDateFrom !== null &&
    isBefore(endStagesDate, endStagesDateFrom)
  ) {
    return false;
  }
  if (endStagesDateTo !== null && isAfter(endStagesDate, endStagesDateTo)) {
    return false;
  }
  return true;
};

/**
 * 農地タイルのpropertiesを、指定された農地フィルタで判定する関数に変換する。
 */
export const buildFarmlandFilterMatcher = (
  filter: FarmlandFilterValues
): ((properties: FarmlandTileProperties | null | undefined) => boolean) => {
  const categoricalFilters = FARMLAND_CATEGORICAL_FILTER_CONFIGS.map(
    (config) => ({
      tilePropertyKey: config.tilePropertyKey,
      selectedValues: selectedValueSet(config, filter[config.filterKey]),
    })
  ).filter((config) => config.selectedValues.size > 0);
  const areaFrom = toSquareMeter(filter.areaFrom, filter.areaUnit);
  const areaTo = toSquareMeter(filter.areaTo, filter.areaUnit);

  return (properties: FarmlandTileProperties | null | undefined): boolean => {
    if (properties === null || properties === undefined) return false;

    const matchesCategoricalFilters = categoricalFilters.every((config) => {
      const propertyValue = normalizeValue(properties[config.tilePropertyKey]);
      return propertyValue ? config.selectedValues.has(propertyValue) : false;
    });

    return (
      matchesCategoricalFilters &&
      matchesAreaFilter(properties, areaFrom, areaTo) &&
      matchesEndStagesDateFilter(properties, filter)
    );
  };
};

/**
 * 農地タイルのpropertiesが、指定された農地フィルタに一致するか判定する。
 */
export const matchesFarmlandFilter = (
  properties: FarmlandTileProperties | null | undefined,
  filter: FarmlandFilterValues
): boolean => buildFarmlandFilterMatcher(filter)(properties);

/**
 * 農地フィルタに、初期値以外の条件が設定されているか判定する。
 */
export const hasActiveFarmlandFilter = (
  filter: FarmlandFilterValues
): boolean => {
  return (
    FARMLAND_CATEGORICAL_FILTER_CONFIGS.some(
      (config) => filter[config.filterKey].length > 0
    ) ||
    filter.areaFrom !== DEFAULT_FARMLAND_FILTER.areaFrom ||
    filter.areaTo !== DEFAULT_FARMLAND_FILTER.areaTo ||
    filter.endStagesDateFrom !== DEFAULT_FARMLAND_FILTER.endStagesDateFrom ||
    filter.endStagesDateTo !== DEFAULT_FARMLAND_FILTER.endStagesDateTo
  );
};
